<script setup>
import { reactive, ref, watch } from 'vue'
import ModalDialog from './ModalDialog.vue'
import LoadingState from './LoadingState.vue'
import { actorName, movementLabels } from '../api/commerce'
import { formatDateTime } from '../utils'

const props = defineProps({
  open: Boolean, item: { type: Object, default: null }, movements: { type: Array, default: () => [] }, loading: Boolean, saving: Boolean, error: { type: String, default: '' },
})
const emit = defineEmits(['close', 'save', 'reload'])
const form = reactive({ movement_type: 'receipt', quantity: 1, reason: '' })
const validationError = ref('')
watch(() => [props.open, props.item?.id], () => {
  Object.assign(form, { movement_type: 'receipt', quantity: 1, reason: '' })
  validationError.value = ''
})
watch(() => props.movements.length, () => { form.quantity = 1; form.reason = ''; validationError.value = '' })

function submit() {
  if (props.saving) return
  const quantity = Number(form.quantity)
  validationError.value = !Number.isInteger(quantity) || quantity < 1 || quantity > 2147483647
    ? 'Укажите целое количество от 1 до 2 147 483 647.'
    : !form.reason.trim() ? 'Укажите основание складской операции.'
      : form.movement_type === 'write_off' && quantity > Number(props.item?.stock_quantity || 0) ? 'На складе недостаточно товара.' : ''
  if (!validationError.value) emit('save', { movement_type: form.movement_type, quantity, reason: form.reason.trim() })
}
</script>

<template>
  <ModalDialog :open="open" :title="`Склад · ${item?.name || ''}`" eyebrow="Движения сохраняются без удаления" wide @close="!saving && emit('close')">
    <p class="stock-balance">Текущий остаток: <strong>{{ item?.stock_quantity || 0 }} {{ item?.unit }}</strong></p>
    <form class="dialog-form stock-form" @submit.prevent="submit">
      <fieldset class="stock-fields" :disabled="saving">
        <div class="form-grid form-grid--2">
          <label class="field"><span>Операция</span><select v-model="form.movement_type"><option value="receipt">Приход</option><option value="write_off">Списание</option></select></label>
          <label class="field"><span>Количество</span><input v-model="form.quantity" type="number" min="1" max="2147483647" step="1" required /></label>
        </div>
        <label class="field"><span>Основание <b aria-hidden="true">*</b></span><input v-model="form.reason" maxlength="1000" required placeholder="Накладная, инвентаризация, порча и т. п." /></label>
      </fieldset>
      <p v-if="validationError || error" class="form-error" role="alert">{{ validationError || error }}</p>
      <div class="stock-form-actions"><button class="button button--primary" type="submit" :disabled="saving">{{ saving ? 'Проводим…' : 'Провести движение' }}</button></div>
    </form>
    <header class="section-header stock-history-header"><div><h3>История склада</h3><p>Кто, когда и на каком основании изменил остаток.</p></div><button class="button button--secondary button--small" type="button" :disabled="loading || saving" @click="emit('reload')">Обновить</button></header>
    <LoadingState v-if="loading" />
    <div v-else-if="movements.length" class="table-scroll">
      <table class="data-table">
        <caption class="sr-only">История складских движений</caption>
        <thead><tr><th>Когда / кто</th><th>Операция</th><th>Количество</th><th>Остаток</th><th>Основание</th></tr></thead>
        <tbody><tr v-for="movement in movements" :key="movement.id">
          <td data-label="Когда / кто">{{ formatDateTime(movement.created_at) }}<span class="cell-secondary">{{ actorName(movement.created_by) }}</span></td>
          <td data-label="Операция">{{ movementLabels[movement.movement_type] || movement.movement_type }}<span v-if="movement.sale_id" class="cell-secondary">Продажа №{{ movement.sale_id }}</span></td>
          <td data-label="Количество"><strong>{{ Number(movement.quantity) > 0 ? '+' : '' }}{{ movement.quantity }} {{ item?.unit }}</strong></td>
          <td data-label="Остаток">{{ movement.stock_after }}</td>
          <td data-label="Основание">{{ movement.reason || '—' }}</td>
        </tr></tbody>
      </table>
    </div>
    <p v-else class="stock-empty">Движений пока нет. Остаток появится после прихода товара.</p>
  </ModalDialog>
</template>

<style scoped>
.stock-balance { font-size: 1rem; margin: 0 0 20px; }.stock-balance strong { color: var(--forest); }
.stock-fields { padding: 0; border: 0; margin: 0; min-width: 0; }.stock-form { padding-bottom: 22px; border-bottom: 1px solid var(--line); }
.stock-form-actions { display: flex; justify-content: flex-end; }.stock-history-header { margin-top: 24px; }.stock-history-header h3 { margin: 0; }.stock-empty { color: var(--muted); font-size: .8rem; }
</style>
